import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, Archive, X, RefreshCw, FolderOpen, MessageSquare, Brain } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

interface StaleProject {
  project_id: string;
  project_name: string;
  board_name: string;
  board_id: string;
  folder_name: string | null;
  created_at: string;
  last_activity_at: string;
  days_inactive: number;
  session_count: number;
  message_count: number;
  memory_count: number;
}

interface StaleProjectDetectorProps {
  workspaceId: string;
  daysThreshold?: number;
  onProjectsArchived: () => void;
}

export function StaleProjectDetector({
  workspaceId,
  daysThreshold = 28,
  onProjectsArchived
}: StaleProjectDetectorProps) {
  const [staleProjects, setStaleProjects] = useState<StaleProject[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchStaleProjects();
  }, [workspaceId, daysThreshold]);

  const fetchStaleProjects = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_stale_projects', {
        p_workspace_id: workspaceId,
        p_days_threshold: daysThreshold
      });

      if (error) throw error;
      setStaleProjects(data || []);
      
      // Auto-select all stale projects by default
      if (data && data.length > 0) {
        setSelectedProjects(new Set(data.map((p: StaleProject) => p.project_id)));
      }
    } catch (error) {
      console.error('Error fetching stale projects:', error);
      toast.error('Failed to detect stale projects');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedProjects.size === 0) {
      toast.error('No projects selected');
      return;
    }

    try {
      setArchiving(true);
      const projectIds = Array.from(selectedProjects);
      
      const { data, error } = await supabase.rpc('bulk_archive_projects', {
        p_project_ids: projectIds
      });

      if (error) throw error;

      const result = data[0];
      toast.success(`Archived ${result.archived_count} project${result.archived_count !== 1 ? 's' : ''}`);
      
      if (result.failed_ids && result.failed_ids.length > 0) {
        toast.warning(`Failed to archive ${result.failed_ids.length} project${result.failed_ids.length !== 1 ? 's' : ''}`);
      }

      // Refresh the list and notify parent
      await fetchStaleProjects();
      setSelectedProjects(new Set());
      onProjectsArchived();
    } catch (error) {
      console.error('Error bulk archiving:', error);
      toast.error('Failed to archive projects');
    } finally {
      setArchiving(false);
    }
  };

  const toggleProject = (projectId: string) => {
    const newSelected = new Set(selectedProjects);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedProjects(newSelected);
  };

  const toggleAll = () => {
    if (selectedProjects.size === staleProjects.length) {
      setSelectedProjects(new Set());
    } else {
      setSelectedProjects(new Set(staleProjects.map(p => p.project_id)));
    }
  };

  if (loading) {
    return (
      <Card className="p-4 border-warning/50 bg-warning/5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Detecting stale projects...
        </div>
      </Card>
    );
  }

  if (staleProjects.length === 0 || dismissed) {
    return null;
  }

  const weeksInactive = Math.floor(daysThreshold / 7);

  return (
    <Card className="p-4 border-warning/50 bg-warning/5">
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />
            <div>
              <h3 className="font-medium text-sm">Stale Projects Detected</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {staleProjects.length} project{staleProjects.length !== 1 ? 's' : ''} inactive for {weeksInactive}+ weeks
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
            className="h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>

        <div className="space-y-2 max-h-80 overflow-y-auto">
          <div className="flex items-center gap-2 pb-2 border-b">
            <Checkbox
              checked={selectedProjects.size === staleProjects.length}
              onCheckedChange={toggleAll}
              id="select-all-projects"
            />
            <label
              htmlFor="select-all-projects"
              className="text-xs font-medium cursor-pointer"
            >
              Select All ({selectedProjects.size}/{staleProjects.length})
            </label>
          </div>

          {staleProjects.map((project) => (
            <div
              key={project.project_id}
              className="flex items-start gap-2 p-3 rounded hover:bg-muted/50 transition-colors border border-transparent hover:border-border"
            >
              <Checkbox
                checked={selectedProjects.has(project.project_id)}
                onCheckedChange={() => toggleProject(project.project_id)}
                id={project.project_id}
              />
              <label
                htmlFor={project.project_id}
                className="flex-1 cursor-pointer space-y-2"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">
                      {project.project_name}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {Math.floor(project.days_inactive / 7)}w inactive
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Board: {project.board_name}
                    {project.folder_name && (
                      <>
                        {" · "}
                        <FolderOpen className="h-3 w-3 inline" /> {project.folder_name}
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    {project.session_count} session{project.session_count !== 1 ? 's' : ''}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    {project.message_count} msg{project.message_count !== 1 ? 's' : ''}
                  </span>
                  <span className="flex items-center gap-1">
                    <Brain className="h-3 w-3" />
                    {project.memory_count} memor{project.memory_count !== 1 ? 'ies' : 'y'}
                  </span>
                </div>

                <div className="text-xs text-muted-foreground">
                  Last active {formatDistanceToNow(new Date(project.last_activity_at), { addSuffix: true })}
                </div>
              </label>
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-2 border-t">
          <Button
            onClick={handleBulkArchive}
            disabled={selectedProjects.size === 0 || archiving}
            size="sm"
            variant="default"
            className="flex-1"
          >
            <Archive className="h-3 w-3 mr-2" />
            Archive Selected ({selectedProjects.size})
          </Button>
          <Button
            onClick={fetchStaleProjects}
            disabled={loading || archiving}
            size="sm"
            variant="outline"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
    </Card>
  );
}
